import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { IconBook, IconCreditCard, IconKey, IconSparkles } from '@tabler/icons-react';
import { List, Title } from '@mantine/core';
import { Footer } from '../../components/Footer';
import ScrollToTop from '../../components/ScrollToTop';

function TermsAndConditions() {
  useEffect(() => {
    document.title = 'Terms and Conditions | PromptSlate';
  }, []);

  return (
    <div className="flex flex-col w-full items-center h-fit">
      <ScrollToTop />
      <div className="w-full h-[45px] flex justify-center border-b">
        <div className="w-full max-w-5xl h-full flex items-center justify-between text-sm">
          <div>Introducting PromptSlate</div>
          <Link to="/signup">
            <div className="flex flex-row">
              Get started now <IconSparkles size="1rem" className="ml-2 mt-0.5" />
            </div>
          </Link>
        </div>
      </div>
      <div className="w-full max-w-5xl mt-2 mb-12">
        <div className="w-full max-w-5xl h-[70px] flex items-center justify-between">
          <div>
            <Link to="/">
              <img
                src="/logo_black.png"
                alt="PromptSlate logo"
                className="h-[30px] object-contain"
              />
            </Link>
          </div>
          <div className="flex flex-row">
            <div className="flex flex-row mr-5 p-2 rounded-lg hover:bg-slate-100 cursor-pointer duration-100 ease-in-out">
              <IconBook size="1.5rem" className="mr-1" /> Docs
            </div>
            <div className="flex flex-row mr-4 p-2 rounded-lg hover:bg-slate-100 cursor-pointer duration-100 ease-in-out">
              <IconCreditCard size="1.5rem" className="mr-1" /> Pricing
            </div>
            <Link to="/login">
              <div className="flex flex-row p-2 rounded-lg hover:bg-slate-100 cursor-pointer duration-100 ease-in-out">
                <IconKey size="1.5rem" className="mr-1" /> Login
              </div>
            </Link>
          </div>
        </div>
        <div className="mt-12">
          <div className="max-w-[50%] text-5xl font-semibold leading-[55px]">
            Terms & Conditions
          </div>
          <span>By continuing to use our platform, you agree to these terms and conditions.</span>
          <div className="mt-8 flex flex-col gap-4 text-lg">
            <Title order={1}>Introduction</Title>
            <p>
              Welcome to PromptSlate ("we", "our", "us"). By accessing or using our website located
              at promptslate.com (the "Website"), you agree to be bound by these Terms and
              Conditions ("Terms"). If you do not agree with these Terms, please do not use our
              Website, but please let us know by emailing to support@promptslate.com so we can try
              to find a solution.
            </p>
            <p>
              By creating an account, logging in, or using any part of the Website, you confirm that
              you are at least 18 years old (or the legal age in your jurisdiction) and that you
              agree to comply with these Terms.
            </p>
            <p>
              Our Privacy Policy also governs your use of our Service and explains how we collect,
              safeguard and disclose information that results from your use of our web pages. Please
              read it here https://promptslate.com/privacy.
            </p>
            <p>
              Your agreement with us includes these Terms and our Privacy Policy (“Agreements”). You
              acknowledge that you have read and understood Agreements, and agree to be bound of
              them.
            </p>
            <Title order={1}>Communications</Title>
            <p>
              By creating an Account on our Service, you agree to subscribe to newsletters,
              marketing or promotional materials and other information we may send. However, you may
              opt out of receiving any, or all, of these communications from us by following the
              unsubscribe link or by emailing at.
            </p>
            <Title order={1}>Purchases</Title>
            <p>
              If you wish to purchase any product or service made available through Service
              (“Purchase”), you may be asked to supply certain information relevant to your Purchase
              including, without limitation, your credit card number, the expiration date of your
              credit card, your billing address, and your shipping information.
            </p>
            <p>
              You represent and warrant that: (i) you have the legal right to use any credit card(s)
              or other payment method(s) in connection with any Purchase; and that (ii) the
              information you supply to us is true, correct and complete.
            </p>
            <p>
              We may employ the use of third party services for the purpose of facilitating payment
              and the completion of Purchases. By submitting your information, you grant us the
              right to provide the information to these third parties subject to our Privacy Policy.
            </p>
            <p>
              We reserve the right to refuse or cancel your order at any time for reasons including
              but not limited to: product or service availability, errors in the description or
              price of the product or service, error in your order or other reasons.
            </p>
            <p>
              We reserve the right to modify the number of requests per month or impose any usage
              restrictions regardless of what was previously committed at the time of subscription.
              These changes will be made according to our system capacity to safeguard the interests
              of all users and to ensure uniform access to the service. We take this step to provide
              equal opportunity to every user and protect the quality of our service.
            </p>
            <p>
              We reserve the right to refuse or cancel your order if fraud or an unauthorized or
              illegal transaction is suspected.
            </p>
            <Title order={1}>Contests, Sweepstakes and Promotions</Title>
            <p>
              Any contests, sweepstakes or other promotions (collectively, “Promotions”) made
              available through Service may be governed by rules that are separate from these Terms
              of Service. If you participate in any Promotions, please review the applicable rules
              as well as our Privacy Policy. If the rules for a Promotion conflict with these Terms
              of Service, Promotion rules will apply.
            </p>
            <Title order={1}>Subscriptions</Title>
            <p>
              Some parts of Service are billed on a subscription basis (“Subscription(s)”). You will
              be billed in advance on a recurring and periodic basis (“Billing Cycle”). Billing
              cycles are set either on a monthly or quarterly or annual basis, depending on the type
              of subscription plan you select when purchasing a Subscription.
            </p>
            <p>
              At the end of each Billing Cycle, your Subscription will automatically renew under the
              exact same conditions unless you cancel it or PromptSlate cancels it. You may cancel
              your Subscription renewal either through your online account management page or by
              contacting PromptSlate customer support team.
            </p>
            <p>
              A valid payment method, including credit card or PayPal, is required to process the
              payment for your subscription. You shall provide PromptSlate with accurate and
              complete billing information including full name, address, state, zip code, telephone
              number, and a valid payment method information. By submitting such payment
              information, you automatically authorize PromptSlate to charge all Subscription fees
              incurred through your account to any such payment instruments.
            </p>
            <p>
              Should automatic billing fail to occur for any reason, PromptSlate will issue an
              electronic invoice indicating that you must proceed manually, within a certain
              deadline date, with the full payment corresponding to the billing period as indicated
              on the invoice.
            </p>
            <Title order={1}>Free Trial</Title>
            <p>
              PromptSlate may, at its sole discretion, offer a Subscription with a free trial for a
              limited period of time (“Free Trial”).
            </p>
            <p>
              You may be required to enter your billing information in order to sign up for Free
              Trial.
            </p>
            <p>
              If you do enter your billing information when signing up for Free Trial, you will not
              be charged by PromptSlate until Free Trial has expired. On the last day of Free Trial
              period, unless you cancelled your Subscription, you will be automatically charged the
              applicable Subscription fees for the type of Subscription you have selected.
            </p>
            <p>
              At any time and without notice, PromptSlate reserves the right to (i) modify Terms of
              Service of Free Trial offer, or (ii) cancel such Free Trial offer.
            </p>
            <Title order={1}>Fee Changes</Title>
            <p>
              PromptSlate, in its sole discretion and at any time, may modify Subscription fees for
              the Subscriptions. Any Subscription fee change will become effective at the end of the
              then-current Billing Cycle.
            </p>
            <p>
              At any time and without notice, PromptSlate reserves the right to (i) modify Terms of
              Service of Free Trial offer, or (ii) cancel such Free Trial offer.
            </p>
            <p>
              Your continued use of Service after Subscription fee change comes into effect
              constitutes your agreement to pay the modified Subscription fee amount.
            </p>
            <Title order={1}>Fair Usage Policy (FUP)</Title>
            <p>
              PromptSlate is committed to providing high-quality, reliable service for all users. We
              expect all our users to avoid misuse or overuse of our services. Overuse by one user
              may impact the quality of service for others. Users are encouraged to select plans
              that align with their professional needs and business size. High-usage users should
              consider upgrading to a more robust plan, requesting a custom plan, or utilizing our
              business API.
            </p>
            <p>
              The vast majority of users (over 95%) consistently operate within the defined
              parameters. Exceeding these parameters could lead to restricted or reduced service
              access, with or without prior warning.
            </p>
            <p>
              Our system diligently monitors for automated or robotic behavior to maintain service
              safety and quality. Please be aware that sharing login details of unlimited accounts
              for monetary gains is considered illegal. Each seat is intended for one individual
              user only, and multiple users sharing one account are not permitted.
            </p>
            <p>
              Please note that unusually high usage or sharing of login details could lead to
              account suspension or deletion without prior notice, with no possibility for refunds.
            </p>
            <Title order={1}>Refund Policy</Title>
            <p>
              All purchases are non-refundable. You can cancel your subscription at any time by
              logging into your account or contacting us using the contact information provided
              below. Your cancellation will take effect at the end of the current paid term.
            </p>
            <p>
              If you are unsatisfied with our services, please email us at support@promptslate.com
            </p>
            <Title order={1}>Prohibited Uses</Title>
            <p>
              You may use Service only for lawful purposes and in accordance with Terms. You agree
              not to use Service:
            </p>
            <List>
              <List.Item>
                In any way that violates any applicable national or international law or regulation.
              </List.Item>
              <List.Item>
                For the purpose of exploiting, harming, or attempting to exploit or harm minors in
                any way by exposing them to inappropriate content or otherwise.
              </List.Item>
              <List.Item>
                To transmit, or procure the sending of, any advertising or promotional material,
                including any “junk mail”, “chain letter,” “spam,” or any other similar
                solicitation.
              </List.Item>
              <List.Item>
                To impersonate or attempt to impersonate Company, a Company employee, another user,
                or any other person or entity.
              </List.Item>
              <List.Item>
                In any way that infringes upon the rights of others, or in any way is illegal,
                threatening, fraudulent, or harmful, or in connection with any unlawful, illegal,
                fraudulent, or harmful purpose or activity.
              </List.Item>
              <List.Item>
                To engage in any other conduct that restricts or inhibits anyone’s use or enjoyment
                of Service, or which, as determined by us, may harm or offend Company or users of
                Service or expose them to liability.
              </List.Item>
            </List>
            <p>Additionally, you agree not to:</p>
            <List>
              <List.Item>
                Use Service in any manner that could disable, overburden, damage, or impair Service
                or interfere with any other party’s use of Service, including their ability to
                engage in real time activities through Service.
              </List.Item>
              <List.Item>
                Use any robot, spider, or other automatic device, process, or means to access
                Service for any purpose, including monitoring or copying any of the material on
                Service.
              </List.Item>
              <List.Item>
                Use any manual process to monitor or copy any of the material on Service or for any
                other unauthorized purpose without our prior written consent.
              </List.Item>
              <List.Item>
                Use any device, software, or routine that interferes with the proper working of
                Service.
              </List.Item>
              <List.Item>
                Introduce any viruses, trojan horses, worms, logic bombs, or other material which is
                malicious or technologically harmful.
              </List.Item>
              <List.Item>
                Attempt to gain unauthorized access to, interfere with, damage, or disrupt any parts
                of Service, the server on which Service is stored, or any server, computer, or
                database connected to Service.
              </List.Item>
              <List.Item>
                Attack Service via a denial-of-service attack or a distributed denial-of-service
                attack.
              </List.Item>
              <List.Item>Take any action that may damage or falsify Company rating.</List.Item>
              <List.Item>
                Otherwise attempt to interfere with the proper working of Service.
              </List.Item>
            </List>
            <Title order={1}>Analytics</Title>
            <p>
              We may use third-party Service Providers to monitor and analyze the use of our
              Service. You can check our privacy policy to know what tools we use.
            </p>
            <Title order={1}>Prohibition of use for Minors</Title>
            <p>
              Service is intended only for access and use by individuals at least eighteen (18)
              years old. By accessing or using any of Company, you warrant and represent that you
              are at least eighteen (18) years of age and with the full authority, right, and
              capacity to enter into this agreement and abide by all of the terms and conditions of
              Terms. If you are not at least eighteen (18) years old, you are prohibited from both
              the access and usage of Service.
            </p>
            <Title order={1}>Accounts</Title>
            <p>
              When you create an account with us, you guarantee that you are above the age of 18,
              and that the information you provide us is accurate, complete, and current at all
              times. Inaccurate, incomplete, or obsolete information may result in the immediate
              termination of your account on Service.
            </p>
            <p>
              You are responsible for maintaining the confidentiality of your account and password,
              including but not limited to the restriction of access to your computer and/or
              account. You agree to accept responsibility for any and all activities or actions that
              occur under your account and/or password, whether your password is with our Service or
              a third-party service. You must notify us immediately upon becoming aware of any
              breach of security or unauthorized use of your account.
            </p>
            <p>
              You may not use as a username the name of another person or entity or that is not
              lawfully available for use, a name or trademark that is subject to any rights of
              another person or entity other than you, without appropriate authorization. You may
              not use as a username any name that is offensive, vulgar or obscene.
            </p>
            <p>
              We reserve the right to refuse service, terminate accounts, remove or edit content, or
              cancel orders in our sole discretion.
            </p>
            <Title order={1}>Intellectual Property</Title>
            <p>
              Service and its original content (excluding Content provided by users), features and
              functionality are and will remain the exclusive property of PromptSlate and its
              licensors. Service is protected by copyright, trademark, and other laws of the United
              States and foreign countries. Our trademarks and trade dress may not be used in
              connection with any product or service without the prior written consent of
              PromptSlate.
            </p>
            <Title order={1}>Copyright Policy</Title>
            <p>
              We respect the intellectual property rights of others. It is our policy to respond to
              any claim that Content posted on Service infringes on the copyright or other
              intellectual property rights (“Infringement”) of any person or entity.
            </p>
            <p>
              If you are a copyright owner, or authorized on behalf of one, and you believe that the
              copyrighted work has been copied in a way that constitutes copyright infringement,
              please submit your claim via email to support@promptslate.com, with the subject line:
              “Copyright Infringement” and include in your claim a detailed description of the
              alleged Infringement as detailed below, under “DMCA Notice and Procedure for Copyright
              Infringement Claims”
            </p>
            <p>
              You may be held accountable for damages (including costs and attorneys' fees) for
              misrepresentation or bad-faith claims on the infringement of any Content found on
              and/or through Service on your copyright.
            </p>
            <Title order={1}>DMCA Notice and Procedure for Copyright Infringement Claims</Title>
            <p>
              You may submit a notification pursuant to the Digital Millennium Copyright Act (DMCA)
              by providing our Copyright Agent with the following information in writing (see 17
              U.S.C 512(c)(3) for further detail):
            </p>
            <List>
              <List.Item>
                an electronic or physical signature of the person authorized to act on behalf of the
                owner of the copyright's interest;
              </List.Item>
              <List.Item>
                a description of the copyrighted work that you claim has been infringed, including
                the URL (i.e., web page address) of the location where the copyrighted work exists
                or a copy of the copyrighted work;
              </List.Item>
              <List.Item>
                identification of the URL or other specific location on Service where the material
                that you claim is infringing is located;
              </List.Item>
              <List.Item>your address, telephone number, and email address;</List.Item>
              <List.Item>
                a statement by you that you have a good faith belief that the disputed use is not
                authorized by the copyright owner, its agent, or the law;
              </List.Item>
              <List.Item>
                a statement by you, made under penalty of perjury, that the above information in
                your notice is accurate and that you are the copyright owner or authorized to act on
                the copyright owner's behalf.
              </List.Item>
            </List>
            <p>You can contact our team via email at support@promptslate.com</p>
            <Title order={1}>Error Reporting and Feedback</Title>
            <p>
              You may provide us either directly at support@promptslate.com or via third party sites
              and tools with information and feedback concerning errors, suggestions for
              improvements, ideas, problems, complaints, and other matters related to our Service
              (“Feedback”). You acknowledge and agree that: (i) you shall not retain, acquire or
              assert any intellectual property right or other right, title or interest in or to the
              Feedback; (ii) Company may have development ideas similar to the Feedback; (iii)
              Feedback does not contain confidential information or proprietary information from you
              or any third party; and (iv) Company is not under any obligation of confidentiality
              with respect to the Feedback. In the event the transfer of the ownership to the
              Feedback is not possible due to applicable mandatory laws, you grant Company and its
              affiliates an exclusive, transferable, irrevocable, free-of-charge, sub-licensable,
              unlimited and perpetual right to use (including copy, modify, create derivative works,
              publish, distribute and commercialize) Feedback in any manner and for any purpose.
            </p>
            <Title order={1}>Links To Other Web Sites</Title>
            <p>
              Our Service may contain links to third party web sites or services that are not owned
              or controlled by PromptSlate
            </p>
            <p>
              PromptSlate has no control over, and assumes no responsibility for the content,
              privacy policies, or practices of any third party web sites or services. We do not
              warrant the offerings of any of these entities/individuals or their websites.
            </p>
            <p>
              You acknowledge and agree that PromptSlate shall not be responsible or liable,
              directly or indirectly, for any damage or loss caused or alleged to be caused by or in
              connection with use of or reliance on any such content, goods or services available on
              or through any such third party web sites or services.
            </p>
            <p>
              We strongly advise you to read the Terms of service and Privacy Policies of any third
              party web sites or services that you visit.
            </p>
            <Title order={1}>Disclaimer Of Warranty</Title>
            <p>
              These services are provided by company on an “as is” and “as available” basis. Company
              makes no representations or warranties of any kind, express or implied, as to the
              operation of their services, or the information, content or materials included
              therein. You expressly agree that your use of these services, their content, and any
              services or items obtained from us is at your sole risk.
            </p>
            <p>
              Neither company nor any person associated with company makes any warranty or
              representation with respect to the completeness, security, reliability, quality,
              accuracy, or availability of the services. Without limiting the foregoing, neither
              company nor anyone associated with company represents or warrants that the services,
              their content, or any services or items obtained through the services will be
              accurate, reliable, error-free, or uninterrupted, that defects will be corrected, that
              the services or the server that makes it available are free of viruses or other
              harmful components or that the services or any services or items obtained through the
              services will otherwise meet your needs or expectations.
            </p>
            <p>
              Company (PromptSlate) hereby disclaims all warranties of any kind, whether express or
              implied, statutory, or otherwise, including but not limited to any warranties of
              merchantability, non-infringement, and fitness for particular purpose.
            </p>
            <p>
              The foregoing does not affect any warranties which cannot be excluded or limited under
              applicable law.
            </p>
            <Title order={1}>Limitation Of Liability</Title>
            <p>
              Except as prohibited by law, you will hold us and our officers, directors, employees,
              and agents harmless for any indirect, punitive, special, incidental, or consequential
              damage, however it arises (including attorneys' fees and all related costs and
              expenses of litigation and arbitration, or at trial or on appeal, if any, whether or
              not litigation or arbitration is instituted), whether in an action of contract,
              negligence, or other tortious action, or arising out of or in connection with this
              agreement, including without limitation any claim for personal injury or property
              damage, arising from this agreement and any violation by you of any federal, state, or
              local laws, statutes, rules, or regulations, even if company has been previously
              advised of the possibility of such damage. Except as prohibited by law, if there is
              liability found on the part of company, it will be limited to the amount paid for the
              products and/or services, and under no circumstances will there be consequential or
              punitive damages. Some states do not allow the exclusion or limitation of punitive,
              incidental or consequential damages, so the prior limitation or exclusion may not
              apply to you.
            </p>
            <Title order={1}>Termination</Title>
            <p>
              We may terminate or suspend your account and bar access to Service immediately,
              without prior notice or liability, under our sole discretion, for any reason
              whatsoever and without limitation, including but not limited to a breach of Terms.
            </p>
            <p>If you wish to terminate your account, you may simply discontinue using Service.</p>
            <Title order={1}>Governing Law</Title>
            <p>
              These Terms shall be governed and construed in accordance with the laws of State of
              Delaware without regard to its conflict of law provisions.
            </p>
            <p>
              Our failure to enforce any right or provision of these Terms will not be considered a
              waiver of those rights. If any provision of these Terms is held to be invalid or
              unenforceable by a court, the remaining provisions of these Terms will remain in
              effect. These Terms constitute the entire agreement between us regarding our Service
              and supersede and replace any prior agreements we might have had between us regarding
              Service.
            </p>
            <Title order={1}>Changes To Service</Title>
            <p>
              We reserve the right to withdraw or amend our Service, and any service or material we
              provide via Service, in our sole discretion without notice. We will not be liable if
              for any reason all or any part of Service is unavailable at any time or for any
              period. From time to time, we may restrict access to some parts of Service, or the
              entire Service, to users, including registered users.
            </p>
            <Title order={1}>Amendments To Terms</Title>
            <p>
              We may amend Terms at any time by posting the amended terms on this site. It is your
              responsibility to review these Terms periodically.
            </p>
            <p>
              Your continued use of the Platform following the posting of revised Terms means that
              you accept and agree to the changes. You are expected to check this page frequently so
              you are aware of any changes, as they are binding on you.
            </p>
            <p>
              By continuing to access or use our Service after any revisions become effective, you
              agree to be bound by the revised terms. If you do not agree to the new terms, you are
              no longer authorized to use Service.
            </p>
            <Title order={1}>Waiver And Severability</Title>
            <p>
              No waiver by Company of any term or condition set forth in Terms shall be deemed a
              further or continuing waiver of such term or condition or a waiver of any other term
              or condition, and any failure of Company to assert a right or provision under Terms
              shall not constitute a waiver of such right or provision.
            </p>
            <p>
              If any provision of Terms is held by a court or other tribunal of competent
              jurisdiction to be invalid, illegal or unenforceable for any reason, such provision
              shall be eliminated or limited to the minimum extent such that the remaining
              provisions of Terms will continue in full force and effect.
            </p>
            <Title order={1}>Acknowledgement</Title>
            <p>
              By using service or other services provided by us, you acknowledge that you have read
              these terms of service and agree to be bound by them.
            </p>
            <Title order={1}>Contact Us</Title>
            <p>You can contact us by emailing to: support@promptslate.com for any queries.</p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default TermsAndConditions;
